import { FC, ReactNode, useState, useContext } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import { SidebarContext } from 'src/core/contexts/SidebarContext';
import { drHerancaBranding } from 'src/drheranca/branding';

import PropTypes from 'prop-types';
import { Button, Badge, Collapse, ListItem, ListItemButton, Tooltip } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface SidebarMenuItemProps {
  children?: ReactNode;
  link?: string;
  icon?: any;
  badge?: string;
  open?: boolean;
  active?: boolean;
  name: string;
  collapsed?: boolean;
  depth?: number;
}

const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
  children,
  link,
  icon: Icon,
  badge,
  open: openParent,
  active,
  name,
  collapsed,
  depth = 0,
  ...rest
}) => {
  const [menuToggle, setMenuToggle] = useState<boolean>(openParent);
  const levelPadding = 1.5 + depth * 1.25;
  const isSubmenuItem = depth > 0;

  const { toggleSidebar } = useContext(SidebarContext);

  const toggleMenu = (): void => {
    setMenuToggle((Open) => !Open);
  };

  if (children) {
    const menuButton = (
      <Button
        className={clsx({ 'Mui-open': menuToggle })}
        component={collapsed && link ? RouterLink : 'button'}
        to={collapsed && link ? link : undefined}
        startIcon={Icon && <Icon />}
        endIcon={
          !collapsed && (menuToggle ? <RemoveIcon /> : <AddIcon />)
        }
        onClick={collapsed ? undefined : toggleMenu}
        fullWidth
        sx={{
          pl: collapsed ? undefined : levelPadding,
          justifyContent: collapsed ? 'center' : 'flex-start',
          minWidth: 0,
          borderRadius: collapsed ? '10px' : '12px',
          color: drHerancaBranding.color.text.menu,
          fontWeight: drHerancaBranding.fontWeight.medium,
          textTransform: 'none',
          '& .MuiButton-startIcon': {
            margin: 0,
          },
          '& .MuiSvgIcon-root': {
            fontSize: collapsed ? 24 : 19,
          },
          '& .MuiButton-endIcon .MuiSvgIcon-root': {
            fontSize: 14,
            color: drHerancaBranding.color.text.secondary,
          },
          '&.Mui-open': {
            color: drHerancaBranding.color.text.primary,
          },
          '&:hover': {
            bgcolor: drHerancaBranding.color.surface.icon,
            color: drHerancaBranding.color.text.primary,
          },
        }}
      >
        {!collapsed && name}
      </Button>
    );

    return (
      <ListItem component="div" className="Mui-children" key={name} {...rest}>
        {collapsed ? (
          <Tooltip title={name} placement="right">
            {menuButton}
          </Tooltip>
        ) : (
          menuButton
        )}
        <Collapse in={menuToggle && !collapsed}>{children}</Collapse>
      </ListItem>
    );
  }

  const menuButton = (
    <ListItemButton
      component={RouterLink}
      to={link || '#'}
      className={active ? 'Mui-active' : ''}
      onClick={collapsed ? undefined : toggleSidebar}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: collapsed ? 0 : 1.25,
        minHeight: 40,
        pl: collapsed ? 1.5 : levelPadding,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: collapsed ? '10px' : '12px',
        color: drHerancaBranding.color.text.menu,
        fontWeight: isSubmenuItem ? 400 : drHerancaBranding.fontWeight.medium,
        minWidth: 0,
        '& .MuiSvgIcon-root': {
          color: 'inherit',
          fontSize: collapsed ? 24 : 19,
        },
        '&.Mui-active': {
          bgcolor: drHerancaBranding.color.surface.icon,
          color: drHerancaBranding.color.text.primary,
          fontWeight: isSubmenuItem ? 400 : drHerancaBranding.fontWeight.semibold,
        },
        '&:hover': {
          bgcolor: drHerancaBranding.color.surface.icon,
          color: drHerancaBranding.color.text.primary,
        },
      }}
    >
      {Icon && <Icon />}
      {!collapsed && <span>{name}</span>}
      {!collapsed && badge && <Badge badgeContent={badge} />}
    </ListItemButton>
  );

  return (
    <ListItem component="div" key={name} disablePadding {...rest}>
      {collapsed ? (
        <Tooltip title={name} placement="right" arrow>
          <span style={{ display: 'flex', width: '100%' }}>
            {menuButton}
          </span>
        </Tooltip>
      ) : (
        menuButton
      )}
    </ListItem>
  );
};

SidebarMenuItem.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
  link: PropTypes.string,
  icon: PropTypes.elementType,
  badge: PropTypes.string,
  open: PropTypes.bool,
  name: PropTypes.string.isRequired
};

SidebarMenuItem.defaultProps = {
  open: false,
  active: false
};

export default SidebarMenuItem;

